import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from '@travelity/form';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
} from '@mui/material';
import {
    type AddProductForm,
    type Attachment,
    type CapacityItem,
    getCapacityDtoToCapacity,
    type Product,
    type ProductCapacityItem,
    productDetailsToUpdateProductDetailsDto,
    type ProductScheduleItem,
    ProductType,
    scheduleDtoToScheduleItem,
    type ScheduleItem,
    useCapacities,
    useSchedules,
    useUpdateProductCapacities,
    useUpdateProductConfiguration,
    useUpdateProductDetails,
    useUpdateProductImages,
    useUpdateProductOptions,
    useUpdateProductPolicies,
    useUpdateProductPricing,
    useUpdateProductRoute,
    useUpdateProductSchedules,
} from '@travelity/api';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LoadingOverlay } from '@travelity/ui';
import { PolicyType } from '@travelity/api/generated';
import {
    productRouteStepFormSchema,
    ProductRouteForm,
} from '@components/product-route-form';
import { ProductDetailsForm } from '@components/product-details';
import detailsSchema from '@components/product-details/product-details.schema';
import { ProductCapacityForm } from '@components/product-capacity-form';
import capacitySchema from '@components/product-capacity-form/product-capacity-form.schema';
import { ProductScheduleForm } from '@components/product-schedule-form';
import scheduleSchema from '@components/product-schedule-form/product-schedule-form.schema';
import { ProductFinancingForm } from '@components/product-financing-form';
import financialSchema from '@components/product-financing-form/product-financing-form.schema';
import { ProductOptionForm } from '@components/product-option-form';
import optionsSchema from '@components/product-option-form/product-option-form.schema';
import { ActivityHistory } from '@components/activity-history';
import { ProductConfigurationForm } from '@components/product-configuration';
import { SidebarContent } from '@components/sidebar-content';
import ProductImagesForm from '@components/product-images-form/product-images-form';
import { ProductPoliciesForm } from '@components/product-policies-form';
import { OnboardingClasses } from '../../../onboarding';
import { useToast } from '../../../hooks';
import AccordionButtons from './accordion-buttons';

export interface ProductEditFormProps {
    product: Product;
    onClose: () => void;
}

type ProductEditFormLocationState =
    | number
    | {
          expanded?: number;
          source?: string;
          readOnly?: boolean;
      };

const formNames = {
    productInfo: 'product details',
    route: 'route',
    capacity: 'capacity',
    schedule: 'schedule',
    financial: 'pricing and financials',
    options: 'options',
};

const ProductEditForm: React.FC<ProductEditFormProps> = ({
    product,
    onClose,
}) => {
    const { t } = useTranslation('product');
    const location = useLocation();
    const state = location.state as ProductEditFormLocationState | undefined;
    const expandedFromState =
        typeof state === 'number' ? state : state?.expanded;
    const isReadOnlyFromNavigation =
        typeof state === 'object' && !!state?.readOnly;
    const [expanded, setExpanded] = useState<number | undefined>(
        expandedFromState ?? 0
    );
    const { canEdit } = product.permissions;
    const canEditInView = canEdit && !isReadOnlyFromNavigation;

    const [editing, setEditing] = useState(false);
    const toggleExpanded = (number: number) => {
        if (editing) return;
        setExpanded(s => (s === number ? undefined : number));
    };
    const onEdit = useCallback(() => setEditing(true), []);

    useEffect(() => {
        if (expandedFromState !== undefined && !editing) {
            setExpanded(expandedFromState);
        }
    }, [expandedFromState, editing]);

    const { showSuccess } = useToast();

    const { mutate: updateConfiguration, isLoading: isUpdatingConfiguration } =
        useUpdateProductConfiguration({
            onSuccess: () => {
                setEditing(false);
                showSuccess('UPDATE_PRODUCT_DETAILS_SUCCESS', {
                    name: product.productInfo.name,
                });
            },
        });
    const {
        Form: ConfigurationForm,
        reset: resetConfiguration,
        formState: { errors: configurationErrors },
    } = useForm({
        defaultValues: product.configuration,
        onSubmit: useCallback(
            (
                data: AddProductForm['configuration'],
                e?: React.BaseSyntheticEvent
            ) => {
                e?.preventDefault();
                updateConfiguration({
                    productId: product.id,
                    data,
                });
            },
            [updateConfiguration, product.id]
        ),
        mode: 'onChange',
    });

    const { mutate: updateDetails, isLoading: isUpdatingDetails } =
        useUpdateProductDetails({
            onSuccess: () => {
                setEditing(false);
                showSuccess('UPDATE_PRODUCT_DETAILS_SUCCESS', {
                    name: product.productInfo.name,
                });
            },
        });
    const {
        Form: DetailsForm,
        reset: resetDetails,
        formState: { errors: detailsErrors },
    } = useForm({
        defaultValues: product.productInfo,
        onSubmit: useCallback(
            (
                data: AddProductForm['productInfo'],
                e?: React.BaseSyntheticEvent
            ) => {
                e?.preventDefault();
                const { languages, ...productData } =
                    productDetailsToUpdateProductDetailsDto({
                        productInfo: data,
                    });
                updateDetails({
                    productId: product.id,
                    data:
                        product.type === ProductType.TRANSFER
                            ? { ...productData, type: ProductType.TRANSFER }
                            : {
                                  ...productData,
                                  type: ProductType.TOUR,
                                  languages,
                              },
                });
            },
            [updateDetails, product.id]
        ),
        mode: 'onChange',
        schema: detailsSchema,
    });

    const { mutate: updateRoute, isLoading: isUpdatingRoute } =
        useUpdateProductRoute(product.route, {
            onSuccess: () => {
                setEditing(false);
                showSuccess('UPDATE_PRODUCT_FORM_SUCCESS', {
                    formName: formNames.route,
                    name: product.productInfo.name,
                });
            },
        });
    const {
        Form: RouteForm,
        reset: resetRoute,
        formState: { errors: routeErrors },
    } = useForm({
        defaultValues: product.route,
        onSubmit: useCallback(
            (data: AddProductForm['route'], e?: React.BaseSyntheticEvent) => {
                e?.preventDefault();
                updateRoute({
                    routeItems: data.stops,
                    productId: product.id,
                    newDistance: data.distance,
                });
            },
            [updateRoute, product.id]
        ),
        mode: 'onChange',
        schema: productRouteStepFormSchema,
    });

    const { mutate: updateCapacities, isLoading: isUpdatingCapacities } =
        useUpdateProductCapacities(product.capacity.capacities, {
            onSuccess: () => {
                setEditing(false);
                showSuccess('UPDATE_PRODUCT_FORM_SUCCESS', {
                    formName: formNames.capacity,
                    name: product.productInfo.name,
                });
            },
        });
    const {
        Form: CapacityForm,
        watch: watchCapacities,
        setValue: setValueCapacities,
        reset: resetCapacity,
        formState: { errors: capacityErrors },
    } = useForm({
        defaultValues: product.capacity,
        onSubmit: useCallback(
            (
                data: AddProductForm['capacity'],
                e?: React.BaseSyntheticEvent
            ) => {
                e?.preventDefault();
                updateCapacities({
                    items: data.capacities,
                    productId: product.id,
                });
            },
            [product]
        ),
        mode: 'onChange',
        schema: capacitySchema,
    });

    const { mutate: updateSchedules, isLoading: isUpdatingSchedules } =
        useUpdateProductSchedules(product.schedule, {
            onSuccess: () => {
                setEditing(false);
                showSuccess('UPDATE_PRODUCT_FORM_SUCCESS', {
                    formName: formNames.schedule,
                    name: product.productInfo.name,
                });
            },
        });
    const {
        Form: ScheduleForm,
        watch: watchSchedules,
        setValue: setValueSchedules,
        reset: resetSchedule,
        formState: { errors: scheduleErrors },
    } = useForm({
        defaultValues: product.schedule,
        onSubmit: useCallback(
            (
                data: AddProductForm['schedule'],
                e?: React.BaseSyntheticEvent
            ) => {
                e?.preventDefault();
                updateSchedules({
                    items: data.items,
                    productId: product.id,
                    swing: data.swing,
                    duration: data.duration,
                });
            },
            [product]
        ),
        mode: 'onChange',
        schema: scheduleSchema,
    });

    const { mutate: updatePricing, isLoading: isUpdatingPricing } =
        useUpdateProductPricing(product.financial, {
            onSuccess: () => {
                setEditing(false);
                showSuccess('UPDATE_PRODUCT_FORM_SUCCESS', {
                    formName: formNames.financial,
                    name: product.productInfo.name,
                });
            },
        });
    const {
        Form: FinancialInformationForm,
        reset: resetFinancial,
        formState: { errors: financialErrors },
    } = useForm({
        defaultValues: product.financial,
        onSubmit: useCallback(
            (
                data: AddProductForm['financial'],
                e?: React.BaseSyntheticEvent
            ) => {
                e?.preventDefault();
                if (data)
                    updatePricing({
                        productId: product.id,
                        data,
                    });
            },
            [updatePricing, product.id]
        ),
        mode: 'onChange',
        context: {
            capacities: product.capacity?.capacities,
        },
        schema: financialSchema,
    });

    const { mutate: updateOptions, isLoading: isUpdatingOptions } =
        useUpdateProductOptions(product.options.items, {
            onSuccess: () => {
                setEditing(false);
                showSuccess('UPDATE_PRODUCT_FORM_SUCCESS', {
                    formName: formNames.options,
                    name: product.productInfo.name,
                });
            },
        });
    const {
        Form: OptionsForm,
        reset: resetOptions,
        formState: { errors: optionsErrors },
    } = useForm({
        defaultValues: product.options,
        onSubmit: useCallback(
            (data: AddProductForm['options'], e?: React.BaseSyntheticEvent) => {
                e?.preventDefault();
                if (data)
                    updateOptions({ productId: product.id, items: data.items });
            },
            [updateOptions, product.id]
        ),
        mode: 'onChange',
        schema: optionsSchema,
    });

    const { mutate: updateImages, isLoading: isUpdatingImages } =
        useUpdateProductImages(product.attachments, {
            onSuccess: () => {
                setEditing(false);
                showSuccess('UPDATE_PRODUCT_IMAGES_SUCCESS', {
                    name: product.productInfo.name,
                });
            },
        });

    const {
        Form: ImagesForm,
        reset: resetImages,
        formState: { errors: imagesErrors },
    } = useForm({
        defaultValues: {
            images: product.attachments,
        },
        onSubmit: useCallback(
            (data: { images: Attachment[] }, e?: React.BaseSyntheticEvent) => {
                e?.preventDefault();
                updateImages({ items: data.images, productId: product.id });
            },
            [product.id]
        ),
        mode: 'onChange',
        // schema: imagesSchema, // Define schema if needed
    });

    const { mutate: updatePolicies, isLoading: isUpdatingPolicies } =
        useUpdateProductPolicies({
            onSuccess: () => {
                setEditing(false);
                showSuccess('UPDATE_PRODUCT_POLICIES_SUCCESS', {
                    name: product.productInfo.name,
                });
            },
        });

    const {
        Form: PoliciesForm,
        reset: resetPolicies,
        formState: { errors: policiesErrors },
    } = useForm({
        defaultValues: {
            policies: (product as any).policies || [],
            policyText: product.policies[0]?.content,
        },
        onSubmit: useCallback(
            (data: { policyText: string }, e?: React.BaseSyntheticEvent) => {
                e?.preventDefault();
                updatePolicies({
                    productId: product.id,
                    data: {
                        items: [
                            {
                                type: PolicyType.PRODUCT_CANCELLATION,
                                enabled: true,
                                description: data.policyText,
                            },
                        ],
                    },
                });
            },
            [product.id, updatePolicies]
        ),
        mode: 'onChange',
    });

    const partialCapacities =
        watchCapacities('capacities')
            // @ts-ignore
            ?.filter((c: CapacityItem) => !c.type)
            .map((c: ProductCapacityItem) => c.capacityId) || [];
    const { isFetching: isCapacitiesFetching } = useCapacities(
        partialCapacities,
        {
            onSuccess: ({ items }) => {
                const oldCapacities = watchCapacities('capacities');
                // @ts-ignore
                const newCapacities = oldCapacities.map((c: CapacityItem) => {
                    if (c.type) return c;
                    const item = items?.find(i => i.id === c.capacityId);
                    if (item) return getCapacityDtoToCapacity(item);
                    return c;
                });
                // @ts-ignore
                setValueCapacities('capacities', newCapacities);
            },
        }
    );

    const partialSchedules =
        watchSchedules('items')
            ?.filter(
                // @ts-ignore
                (s: ScheduleItem) => !s.times?.length && !s.allDay && !s.isNew
            )
            .map((s: ProductScheduleItem) => s.scheduleId) || [];
    const { isFetching: isSchedulesFetching } = useSchedules(partialSchedules, {
        onSuccess: ({ items: its }) => {
            const oldSchedules = watchSchedules('items');
            // @ts-ignore
            const newSchedules = oldSchedules.map((s: ScheduleItem) => {
                if (s.times?.length && !s.allDay) return s;
                // @ts-ignore
                const item = its?.find(i => i.id === s.scheduleId);
                if (item)
                    return { id: s.id, ...scheduleDtoToScheduleItem(item) };
                return s;
            });
            // @ts-ignore
            setValueSchedules('items', newSchedules);
        },
    });

    const reset = useCallback(() => {
        resetConfiguration(product.configuration);
        resetDetails(product.productInfo);
        resetRoute(product.route);
        resetCapacity(product.capacity);
        resetSchedule(product.schedule);
        resetFinancial(product.financial);
        resetOptions(product.options);
        resetImages({ images: product.attachments });
        resetPolicies({
            policies: product.policies || [],
            policyText: product.policies?.[0]?.content || '',
        });
    }, [expanded, product]);

    const onCancel = useCallback(() => {
        reset();
        setEditing(false);
    }, [reset]);

    useEffect(() => {
        reset();
        setEditing(false);
    }, [product]);

    return (
        <SidebarContent
            className={OnboardingClasses.PRODUCT_PREVIEW_PANEL}
            title={t('Product Preview')}
            onClose={onClose}
        >
            <Box
                sx={{
                    '&&& .MuiAccordionSummary-root': {
                        borderBottom: '1px solid #DFE1ED',
                    },
                    '&&& .MuiAccordionDetails-root': {
                        py: 3,
                    },
                }}
            >
                <ConfigurationForm id="productConfiguration">
                    <Accordion
                        disableGutters
                        expanded={expanded === 0}
                        onChange={() => toggleExpanded(0)}
                    >
                        <AccordionSummary>
                            {t('Product Configuration')}
                            {expanded === 0 && canEditInView && (
                                <AccordionButtons
                                    editing={editing}
                                    saving={isUpdatingConfiguration}
                                    errors={configurationErrors}
                                    onEditCancel={onCancel}
                                    onEdit={onEdit}
                                />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <ProductConfigurationForm
                                preview={!editing || expanded !== 0}
                            />
                            {isUpdatingConfiguration && <LoadingOverlay />}
                        </AccordionDetails>
                    </Accordion>
                </ConfigurationForm>
                <DetailsForm id="productInfo">
                    <Accordion
                        disableGutters
                        expanded={expanded === 1}
                        onChange={() => toggleExpanded(1)}
                    >
                        <AccordionSummary>
                            {t('Product Details')}
                            {expanded === 1 && canEditInView && (
                                <AccordionButtons
                                    editing={editing}
                                    saving={isUpdatingDetails}
                                    errors={detailsErrors}
                                    onEditCancel={onCancel}
                                    onEdit={onEdit}
                                />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <ProductDetailsForm
                                type={product.type}
                                preview={!editing || expanded !== 1}
                            />
                            {isUpdatingDetails && <LoadingOverlay />}
                        </AccordionDetails>
                    </Accordion>
                </DetailsForm>
                <RouteForm id="route">
                    <Accordion
                        disableGutters
                        expanded={expanded === 2}
                        onChange={() => toggleExpanded(2)}
                    >
                        <AccordionSummary>
                            {t('Route')}
                            {expanded === 2 && canEditInView && (
                                <AccordionButtons
                                    editing={editing}
                                    saving={isUpdatingRoute}
                                    errors={routeErrors}
                                    onEditCancel={onCancel}
                                    onEdit={onEdit}
                                />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <ProductRouteForm
                                showFull
                                preview={!editing || expanded !== 2}
                            />
                            {isUpdatingRoute && <LoadingOverlay />}
                        </AccordionDetails>
                    </Accordion>
                </RouteForm>
                <CapacityForm id="capacity">
                    <Accordion
                        disableGutters
                        expanded={expanded === 3}
                        onChange={() => toggleExpanded(3)}
                    >
                        <AccordionSummary>
                            {t('Capacity')}
                            {expanded === 3 && canEditInView && (
                                <AccordionButtons
                                    editing={editing}
                                    saving={
                                        isUpdatingCapacities ||
                                        isCapacitiesFetching
                                    }
                                    errors={capacityErrors}
                                    onEditCancel={onCancel}
                                    onEdit={onEdit}
                                />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            {!partialCapacities.length && (
                                <ProductCapacityForm
                                    isUpdate
                                    preview={!editing || expanded !== 3}
                                />
                            )}
                            {(isUpdatingCapacities || isCapacitiesFetching) && (
                                <LoadingOverlay />
                            )}
                        </AccordionDetails>
                    </Accordion>
                </CapacityForm>
                <ScheduleForm id="schedule">
                    <Accordion
                        disableGutters
                        expanded={expanded === 4}
                        onChange={() => toggleExpanded(4)}
                    >
                        <AccordionSummary>
                            {t('Schedule')}
                            {expanded === 4 && canEditInView && (
                                <AccordionButtons
                                    editing={editing}
                                    saving={
                                        isUpdatingSchedules ||
                                        isSchedulesFetching
                                    }
                                    errors={scheduleErrors}
                                    onEditCancel={onCancel}
                                    onEdit={onEdit}
                                />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            {!partialSchedules.length && (
                                <ProductScheduleForm
                                    preview={!editing || expanded !== 4}
                                />
                            )}
                            {(isUpdatingSchedules || isSchedulesFetching) && (
                                <LoadingOverlay />
                            )}
                        </AccordionDetails>
                    </Accordion>
                </ScheduleForm>
                <FinancialInformationForm id="financial">
                    <Accordion
                        disableGutters
                        expanded={expanded === 5}
                        onChange={() => toggleExpanded(5)}
                    >
                        <AccordionSummary>
                            {t('Pricing and Financials')}
                            {expanded === 5 && canEditInView && (
                                <AccordionButtons
                                    editing={editing}
                                    saving={isUpdatingPricing}
                                    errors={financialErrors}
                                    onEditCancel={onCancel}
                                    onEdit={onEdit}
                                />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <ProductFinancingForm
                                preview={!editing || expanded !== 5}
                                capacities={product.capacity?.capacities}
                                schedules={product.schedule?.items}
                                productId={product.id}
                            />
                            {isUpdatingPricing && <LoadingOverlay />}
                        </AccordionDetails>
                    </Accordion>
                </FinancialInformationForm>
                <OptionsForm id="options">
                    <Accordion
                        disableGutters
                        expanded={expanded === 6}
                        onChange={() => toggleExpanded(6)}
                    >
                        <AccordionSummary>
                            {t('Product Options')}
                            {expanded === 6 && canEditInView && (
                                <AccordionButtons
                                    editing={editing}
                                    saving={isUpdatingOptions}
                                    errors={optionsErrors}
                                    onEditCancel={onCancel}
                                    onEdit={onEdit}
                                />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <ProductOptionForm
                                preview={!editing || expanded !== 6}
                                capacities={product.capacity?.capacities}
                                schedules={product.schedule?.items}
                            />
                            {isUpdatingOptions && <LoadingOverlay />}
                        </AccordionDetails>
                    </Accordion>
                </OptionsForm>
                <ImagesForm id="images">
                    <Accordion
                        disableGutters
                        expanded={expanded === 7}
                        onChange={() => toggleExpanded(7)}
                    >
                        <AccordionSummary>
                            {t('Product Images')}
                            {expanded === 7 && canEditInView && (
                                <AccordionButtons
                                    editing={editing}
                                    saving={isUpdatingImages}
                                    errors={imagesErrors}
                                    onEditCancel={onCancel}
                                    onEdit={onEdit}
                                />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <ProductImagesForm
                                preview={!editing || expanded !== 7}
                            />
                            {isUpdatingImages && <LoadingOverlay />}
                        </AccordionDetails>
                    </Accordion>
                </ImagesForm>
                <PoliciesForm id="policies">
                    <Accordion
                        disableGutters
                        expanded={expanded === 8}
                        onChange={() => toggleExpanded(8)}
                    >
                        <AccordionSummary>
                            {t('Product Policies')}
                            {expanded === 8 && canEditInView && (
                                <AccordionButtons
                                    editing={editing}
                                    saving={isUpdatingPolicies}
                                    errors={policiesErrors}
                                    onEditCancel={onCancel}
                                    onEdit={onEdit}
                                />
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <ProductPoliciesForm
                                preview={!editing || expanded !== 8}
                                showErrors={!!policiesErrors}
                            />
                            {isUpdatingPolicies && <LoadingOverlay />}
                        </AccordionDetails>
                    </Accordion>
                </PoliciesForm>
                <Accordion
                    disableGutters
                    expanded={expanded === 9}
                    onChange={() => toggleExpanded(9)}
                >
                    <AccordionSummary>{t('Activity Log')}</AccordionSummary>
                    <AccordionDetails>
                        <ActivityHistory
                            entity="product"
                            lifecycle={product.lifecycle}
                        />
                    </AccordionDetails>
                </Accordion>
            </Box>
        </SidebarContent>
    );
};

export default React.memo(ProductEditForm);
